import { useDraggable } from "@dnd-kit/core";
import { type ComponentType, useId } from "react";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";
import { useAdminFormEditorPaths } from "../../hooks/use-admin-form-editor-paths";
import { PALETTE_ITEMS } from "../../lib/palette-metadata";
import type { PaletteFieldType } from "../../types";
import { translatePaletteFieldTypeLabel } from "./form-builder-palette-label";
import { BUILDER_CONTROL_FOCUS } from "./form-builder-shared-classes";

export const PALETTE_PREFIX = "palette::";

/**
 * Right-hand palette: draggable field types plus click-to-add on the main control.
 */
export function FieldPalette({
	onAddClick,
	titleId: titleIdProp,
}: {
	onAddClick: (fieldType: PaletteFieldType) => void;
	/** When the palette sits in a landmark, pass the parent `useId()` so `aria-labelledby` matches. */
	titleId?: string;
}) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const generatedTitleId = useId();
	const titleId = titleIdProp ?? generatedTitleId;

	return (
		<>
			<header className="border-b border-border-secondary px-6 py-4">
				<h2 id={titleId} className="text-lg font-semibold text-text-primary">
					{t(paths.formEditor.palette.title)}
				</h2>
				<p className="text-sm text-text-tertiary">
					{t(paths.formEditor.palette.subtitle)}
				</p>
			</header>
			<div className="flex flex-1 flex-col gap-2 overflow-y-auto p-4">
				{PALETTE_ITEMS.map(({ type, Icon }) => (
					<PaletteRow
						key={type}
						fieldType={type}
						title={translatePaletteFieldTypeLabel(t, paths, type)}
						Icon={Icon}
						onAdd={() => {
							onAddClick(type);
						}}
						paletteRowKey={paths.formEditor.a11y.paletteRow}
					/>
				))}
			</div>
		</>
	);
}

function PaletteRow({
	fieldType,
	title,
	Icon,
	onAdd,
	paletteRowKey,
}: {
	fieldType: PaletteFieldType;
	title: string;
	Icon: ComponentType<{ className?: string }>;
	onAdd: () => void;
	paletteRowKey: string;
}) {
	const { t } = useAppTranslation();
	const { attributes, listeners, setNodeRef, isDragging } =
		useDraggablePalette(fieldType);

	return (
		<button
			type="button"
			ref={setNodeRef}
			{...listeners}
			{...attributes}
			className={cx(
				"group flex w-full cursor-grab items-center gap-3 rounded-lg border border-border-secondary bg-background-primary p-3 text-left transition-colors active:cursor-grabbing",
				"hover:border-brand-500 hover:bg-brand-50",
				BUILDER_CONTROL_FOCUS,
				isDragging && "opacity-50",
			)}
			onClick={onAdd}
			data-testid={`form-builder-palette-${fieldType}`}
			aria-label={t(paletteRowKey as never, {
				label: title,
			})}
		>
			<span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-background-primary transition-colors group-hover:bg-brand-500">
				<Icon
					className="size-4 text-fg-secondary group-hover:text-white"
					aria-hidden
				/>
			</span>
			<span className="min-w-0 flex-1 font-medium text-text-primary">
				{title}
			</span>
		</button>
	);
}

function useDraggablePalette(fieldType: PaletteFieldType) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: `${PALETTE_PREFIX}${fieldType}`,
		data: { type: "FIELD_TYPE", fieldType },
	});
	return { attributes, listeners, setNodeRef, isDragging };
}
